import { ActionType } from '../../../core/redux/actionType';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../core/api/module';
import * as R from 'ramda';
import { Endpoints } from '../../../core/api/endpoints';
import { Rater } from './models';
import { AppState } from '../../../core/redux/appState';
import { toMap } from '../../../language/arrays';


const KEY_ACTION = ActionType.AddRaterByIdRequest;
const ACTIONS    = [
  ActionType.AddRaterByIdRequest,
  ActionType.AddRaterByIdResponse,
  ActionType.AddRaterByIdError,
];


type Payload = Rater;


type Meta = {
  raterId: number;
  projectId: number;
};


/**
 * Dispatch this associate a new rater with the specified project
 *
 * @param projectId - the database Id for the project to which this person should be added
 * @param participantId - the rater's participant Id
 * @param raterId - the rater's database Id
 * @param levelId - the rater's permissions level
 */
export const addRaterByIdAction = (
  projectId: number,
  participantId: number,
  raterId: number,
  levelId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.AddRaterById,
    headers:  { 'content-type': 'application/json' },
    body: (state: AppState) => JSON.stringify({
      projectId:     projectId,
      participantId: participantId,
      raterId:       raterId,
      levelId:       levelId,
      pageLanguageId: state.locale.lang.languageId,
    }),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        raterId:   raterId,
        projectId: projectId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${raterId}-${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.raterId}-${action.meta.projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const requirements        = toMap(successState.participantRequirements || [], req => `${req.surveyLevelId}`);
  const requirementToUpdate = requirements[payload!.levelId];

  const withRaterAdded = R.assocPath(
    ['projectRaters', `${payload!.projectId}`, `${payload!.raterId}`],
    payload,
    successState,
  );

  if (!requirementToUpdate) {
    return withRaterAdded;
  }

  const ratersWithUpdatedCount = R.assocPath(
    [`${payload?.levelId}`, 'raterCount'],
    requirementToUpdate.raterCount + 1,
    requirements,
  );

  return R.assoc('participantRequirements', Object.values(ratersWithUpdatedCount), withRaterAdded);
};

export const addRaterByIdReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
